"use client";
import { useEffect, useId, useRef, useState } from "react";
export default function ProjectForm({
  situation = "",
  secteur = "",
}: {
  situation?: string;
  secteur?: string;
}) {
  const id = useId();
  const [ready, setReady] = useState(false);
  useEffect(() => setReady(true), []);
  const [step, setStep] = useState(0);
  const [values, setValues] = useState<Record<string, string>>({
    situation,
    secteur,
  });
  const [files, setFiles] = useState<File[]>([]);
  const [review, setReview] = useState(false);
  const [error, setError] = useState("");
  const heading = useRef<HTMLHeadingElement>(null);
  function addFiles(input: HTMLInputElement) {
    const selected = Array.from(input.files || []);
    setFiles((previous) => {
      const merged = [...previous];
      for (const file of selected) {
        if (
          !merged.some(
            (f) =>
              f.name === file.name &&
              f.size === file.size &&
              f.lastModified === file.lastModified,
          )
        )
          merged.push(file);
      }
      return merged;
    });
    input.value = "";
    setReview(false);
    setError("");
  }

  const labels = [
    "Votre situation",
    "Votre vérin",
    "Vos fichiers",
    "Vos coordonnées",
  ];
  function move(next: number) {
    setStep(next);
    setError("");
    setReview(false);
    requestAnimationFrame(() => heading.current?.focus());
  }
  function field(name: string, label: string, type = "text", required = false) {
    return (
      <label key={name} htmlFor={`${id}-${name}`}>
        {label}
        {required ? " *" : ""}
        <input
          id={`${id}-${name}`}
          name={name}
          type={type}
          required={required}
          maxLength={300}
          value={values[name] || ""}
          onChange={(e) => {
            setValues({ ...values, [name]: e.target.value });
            setReview(false);
          }}
        />
      </label>
    );
  }
  return (
    <form
      className="cro-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (step === 0 && !values.situation) {
          setError("Choisissez votre situation.");
          return;
        }
        if (step < 3) {
          move(step + 1);
          return;
        }
        if (!values.email?.trim() && !values.telephone?.trim()) {
          setError("Renseignez un e-mail ou un téléphone.");
          return;
        }
        setError("");
        setReview(true);
      }}
    >
      <p className="cro-notice">
        Démonstration : aucun envoi n’est disponible. Les données et fichiers
        restent dans cette page et disparaissent à sa fermeture.
      </p>
      <ol className="cro-steps" aria-label="Étapes de la demande">
        {labels.map((label, i) => (
          <li key={label} aria-current={i === step ? "step" : undefined}>
            <b>{i + 1}</b>
            <span>
              {["Situation", "Votre vérin", "Fichiers", "Coordonnées"][i]}
            </span>
          </li>
        ))}
      </ol>
      <h3 ref={heading} tabIndex={-1}>
        {" "}
        {step + 1}. {labels[step]}
      </h3>
      {step === 0 && (
        <fieldset>
          <legend>Quel est votre besoin ?</legend>
          {[
            ["neuf", "Un vérin à concevoir"],
            ["renovation", "Un vérin à remettre en état ou à améliorer"],
            ["arret", "Un vérin à l’arrêt"],
          ].map(([value, label]) => (
            <label className="cro-choice" key={value}>
              <input
                type="radio"
                name="situation"
                value={value}
                checked={values.situation === value}
                onChange={() => setValues({ ...values, situation: value })}
              />
              {label}
            </label>
          ))}
          <label className="cro-choice">
            <input
              type="checkbox"
              checked={values.catalogue === "oui"}
              onChange={(e) =>
                setValues({
                  ...values,
                  catalogue: e.target.checked ? "oui" : "",
                })
              }
            />
            Vérin de catalogue, normé ou pneumatique
          </label>
          {values.catalogue && (
            <p>
              SECMHY se concentre sur les vérins hydrauliques spécifiques.
              Précisez votre besoin afin d’en vérifier l’adéquation.
            </p>
          )}
          {values.situation === "arret" && (
            <p>
              Pour un vérin à l’arrêt, privilégiez un échange téléphonique. Le
              délai d’intervention doit être convenu avec l’atelier.
            </p>
          )}
        </fieldset>
      )}
      {step === 1 && (
        <>
          <p>
            Tous ces renseignements sont facultatifs. Un plan n’est pas
            nécessaire pour commencer l’échange.
          </p>
          <div className="cro-fields">
            {[
              ["secteur", "Secteur"],
              ["application", "Application et machine"],
              ["effort", "Effort"],
              ["course", "Course"],
              ["pression", "Pression"],
              ["fixation", "Fixation"],
              ["environnement", "Environnement"],
              ["fluide", "Fluide"],
              ["quantite", "Quantité"],
              ["usage", "Intensité d’usage"],
              ["delai", "Délai souhaité"],
              ["serie", "Référence ou numéro de série"],
            ].map(([n, l]) => field(n, l))}
          </div>
        </>
      )}
      <div hidden={step !== 2}>
        <label>
          Prendre une photo
          <input
            type="file"
            accept="image/*"
            capture="environment"
            onChange={(e) => {
              addFiles(e.currentTarget);
            }}
          />
        </label>
        <p>
          Plan, croquis, photo de la plaque ou du vérin. Sélection locale
          uniquement.
        </p>
        <label htmlFor={`${id}-files`}>
          Choisir des fichiers
          <input
            id={`${id}-files`}
            type="file"
            multiple
            accept="image/*,.pdf,.dxf,.dwg,.step,.stp"
            onChange={(e) => {
              addFiles(e.currentTarget);
            }}
          />
        </label>
        <ul>
          {files.map((f, i) => (
            <li key={`${f.name}-${i}`}>
              {f.name} · {Math.ceil(f.size / 1024)} Ko{" "}
              <button
                type="button"
                className="file-remove"
                aria-label={`Retirer ${f.name}`}
                onClick={() => {
                  setFiles((previous) =>
                    previous.filter((_, index) => index !== i),
                  );
                  setReview(false);
                }}
              >
                Retirer
              </button>
            </li>
          ))}
        </ul>
      </div>
      {step === 3 && (
        <>
          <div className="cro-fields">
            {field("nom", "Nom", "text", true)}
            {field("societe", "Société", "text", true)}
            {field("email", "E-mail", "email")}
            {field("telephone", "Téléphone", "tel")}
          </div>
          <p>Au moins un moyen de contact est nécessaire.</p>
          <label>
            Comment avez-vous connu SECMHY ?
            <select
              value={values.origine || ""}
              onChange={(e) =>
                setValues({ ...values, origine: e.target.value })
              }
            >
              <option value="">Facultatif</option>
              {[
                "Recommandation",
                "Plaque sur un vérin",
                "Recherche",
                "Déjà client",
              ].map((x) => (
                <option key={x}>{x}</option>
              ))}
            </select>
          </label>
        </>
      )}
      <p role="alert">{error}</p>
      <div className="cro-actions">
        {step === 1 && (
          <button
            type="button"
            className="cro-button secondary"
            onClick={() => move(2)}
          >
            Passer les caractéristiques
          </button>
        )}
        {step > 0 && (
          <button
            type="button"
            className="cro-button secondary"
            onClick={() => move(step - 1)}
          >
            Retour
          </button>
        )}
        <button className="cro-button" type="submit" disabled={!ready}>
          {step < 3 ? "Continuer" : "Vérifier ma demande"}
        </button>
      </div>
      {review && (
        <div role="status" className="cro-notice">
          <strong>Votre saisie est complète. Aucun envoi n’a eu lieu.</strong>
          <p>
            {values.nom} · {values.societe} · {values.email || values.telephone}
          </p>
          <p>
            {files.length} fichier(s) sélectionné(s). L’envoi sera disponible
            dans une prochaine version.
          </p>
        </div>
      )}
    </form>
  );
}
